/**
 * 四半期ビューのチーム別戦闘力テーブル。
 *
 * Q の全月 (通常 3 ヶ月) について、メンバーごとに期待値と実績戦闘力を月別に
 * 横並びで表示する。末尾に Q 総計列 (稼働日加重: Σ実績Pt ÷ Σ稼働日 / Σ(期待値×稼働日) ÷ Σ稼働日)
 * を持つ。実績の `完了(処理中含め)` 表記は月別ビュー (TeamTable) とフォーマッタを共有する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 四半期ビューの戦闘力テーブル (メンバー × 月別の期待値/実績 + Q総計)
 * @graph-connects none
 */

import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import type { MonthlyCombatPower, MonthData } from '../../../types/combat-power.js';
import { formatNum, formatActualPowerDisplay } from './combat-power-utils.js';

/**
 * メンバー 1 名の Q 総計 (稼働日加重) を計算する。
 * - 期待値: Σ(期待値 × 稼働日) ÷ Σ稼働日 (期待値がある月のみ)
 * - 実績: Σ実績Pt ÷ Σ稼働日。backlog がある月は 完了 / 完了+処理中 を分けて集計し、
 *   backlog が無い月はシート由来 actualPower × 稼働日 で補完する
 * @graph-connects none
 */
function quarterTotalOf(months: (MonthData | undefined)[]): {
  expected: number | null;
  completedPower: number | null;
  totalPower: number | null;
  hasWip: boolean;
} {
  let expectedWeighted = 0;
  let expectedDays = 0;
  let completedPt = 0;
  let totalPt = 0;
  let actualDays = 0;
  let hasActual = false;
  let hasWip = false;
  for (const cm of months) {
    if (!cm || cm.businessDays <= 0) continue;
    if (cm.expectedPower !== null) {
      expectedWeighted += cm.expectedPower * cm.businessDays;
      expectedDays += cm.businessDays;
    }
    if (cm.backlogPt) {
      completedPt += cm.backlogPt.completed;
      totalPt += cm.backlogPt.completed + cm.backlogPt.inProgress;
      if (cm.backlogPt.inProgress > 0) hasWip = true;
      actualDays += cm.businessDays;
      hasActual = true;
    } else if (cm.actualPower !== null) {
      completedPt += cm.actualPower * cm.businessDays;
      totalPt += cm.actualPower * cm.businessDays;
      actualDays += cm.businessDays;
      hasActual = true;
    }
  }
  return {
    expected: expectedDays > 0 ? expectedWeighted / expectedDays : null,
    completedPower: hasActual && actualDays > 0 ? completedPt / actualDays : null,
    totalPower: hasActual && actualDays > 0 ? totalPt / actualDays : null,
    hasWip,
  };
}

/**
 * `完了(処理中含め)` 形式の Q 総計・チーム平均表示を組み立てる。
 * @graph-connects none
 */
function formatPowerPair(
  completedPower: number | null,
  totalPower: number | null,
  hasWip: boolean,
): string {
  if (completedPower === null) return '-';
  return hasWip && totalPower !== null
    ? `${formatNum(completedPower, 1)}(${formatNum(totalPower, 1)})`
    : formatNum(completedPower, 1);
}

/**
 * チーム合計行の単月平均。TeamTable の合計行と同じ規則で、backlog を持つメンバーが
 * 1 名でもいればそのメンバーだけの平均、いなければシート実績の平均にフォールバックする。
 * @graph-connects none
 */
function teamMonthAverage(
  members: MonthlyCombatPower[],
  monthIdx: number,
): { completedPower: number | null; totalPower: number | null; hasWip: boolean } {
  const withBacklog = members.filter((m) => {
    const cm = m.months[monthIdx];
    return cm?.backlogPt && cm.businessDays > 0;
  });
  if (withBacklog.length === 0) {
    const withActual = members.filter((m) => m.months[monthIdx]?.actualPower !== null);
    if (withActual.length === 0) return { completedPower: null, totalPower: null, hasWip: false };
    const avg =
      withActual.reduce((s, m) => s + (m.months[monthIdx]?.actualPower ?? 0), 0) /
      withActual.length;
    return { completedPower: avg, totalPower: avg, hasWip: false };
  }
  let sumCompleted = 0;
  let sumTotal = 0;
  let hasWip = false;
  for (const m of withBacklog) {
    const cm = m.months[monthIdx];
    const bp = cm?.backlogPt;
    if (!cm || !bp) continue;
    sumCompleted += bp.completed / cm.businessDays;
    sumTotal += (bp.completed + bp.inProgress) / cm.businessDays;
    if (bp.inProgress > 0) hasWip = true;
  }
  return {
    completedPower: sumCompleted / withBacklog.length,
    totalPower: sumTotal / withBacklog.length,
    hasWip,
  };
}

interface QuarterTeamTableProps {
  teamLabel: string;
  members: MonthlyCombatPower[];
  /** Q の月見出し (例 ["2026-4月", "2026-5月", "2026-6月"])。列順の SSOT */
  monthHeaders: string[];
  /**
   * 担当者名 (小文字 nickName) → メールアドレスの対応表。
   * 引き当てられた担当者名はメンバー詳細ページ (`/members/$email`) へのリンクになる。
   */
  nameToEmail?: Map<string, string>;
  /** チーム代表色 (6 桁 HEX)。指定すると見出しを代表色の帯で塗る (TeamTable と同じ) */
  accentColor?: string;
  /** 担当者が 0 名のとき表示する空メッセージ (未指定なら空行を出さない) */
  emptyMessage?: string;
}

/** @graph-connects none */
export function QuarterTeamTable({
  teamLabel,
  members,
  monthHeaders,
  nameToEmail,
  accentColor,
  emptyMessage,
}: QuarterTeamTableProps) {
  const { t } = useTranslation();

  // チーム合計行: 単月平均 (TeamTable と同規則) + Q 総計平均
  const monthAverages = monthHeaders.map((_, i) => teamMonthAverage(members, i));
  const quarterTotals = members.map((m) => quarterTotalOf(m.months));
  const totalsWithExpected = quarterTotals.filter((q) => q.expected !== null);
  const avgExpectedTotal =
    totalsWithExpected.length > 0
      ? totalsWithExpected.reduce((s, q) => s + (q.expected ?? 0), 0) / totalsWithExpected.length
      : null;
  const totalsWithActual = quarterTotals.filter((q) => q.completedPower !== null);
  const avgQuarterActual =
    totalsWithActual.length > 0
      ? {
          completedPower:
            totalsWithActual.reduce((s, q) => s + (q.completedPower ?? 0), 0) /
            totalsWithActual.length,
          totalPower:
            totalsWithActual.reduce((s, q) => s + (q.totalPower ?? 0), 0) / totalsWithActual.length,
          hasWip: totalsWithActual.some((q) => q.hasWip),
        }
      : { completedPower: null, totalPower: null, hasWip: false };
  const monthExpectedAverages = monthHeaders.map((_, i) => {
    const withExpected = members.filter((m) => m.months[i]?.expectedPower !== null);
    if (withExpected.length === 0) return null;
    return (
      withExpected.reduce((s, m) => s + (m.months[i]?.expectedPower ?? 0), 0) / withExpected.length
    );
  });

  return (
    <div className="flex-1 min-w-0">
      {accentColor ? (
        // ACE サブチーム別表示: 代表色の帯 (左ボーダー + 薄い背景) で色分けする
        <div
          className="mb-3 rounded-md px-3 py-1.5"
          style={{ backgroundColor: `${accentColor}1A`, borderLeft: `4px solid ${accentColor}` }}
        >
          <h3 className="text-base font-bold" style={{ color: accentColor }}>
            {teamLabel}
          </h3>
        </div>
      ) : (
        <div className="flex items-center justify-between mb-3 gap-2">
          <h3 className="text-lg font-bold text-[var(--color-text-primary)]">{teamLabel}</h3>
        </div>
      )}
      {members.length === 0 && emptyMessage ? (
        <p className="py-6 text-center text-xs text-[var(--color-text-muted)]">{emptyMessage}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th
                  rowSpan={2}
                  className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] align-bottom"
                >
                  {t('members.combatPower.name')}
                </th>
                {monthHeaders.map((mh) => (
                  <th
                    key={mh}
                    colSpan={2}
                    className="text-center py-1.5 px-2 font-medium text-[var(--color-text-secondary)] border-l border-[var(--color-border)] whitespace-nowrap"
                  >
                    {mh}
                  </th>
                ))}
                <th
                  colSpan={2}
                  className="text-center py-1.5 px-2 font-medium text-[var(--color-text-secondary)] border-l border-[var(--color-border)] whitespace-nowrap"
                >
                  {t('members.combatPower.quarterTotal')}
                </th>
              </tr>
              <tr className="border-b-2 border-[var(--color-border)]">
                {[...monthHeaders, '__total__'].map((key) => (
                  <Fragment key={key}>
                    <th className="text-right py-1.5 px-2 font-medium text-[var(--color-text-muted)] border-l border-[var(--color-border)] whitespace-nowrap">
                      {t('members.combatPower.expectedLabel')}
                    </th>
                    <th className="text-right py-1.5 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                      {t('members.combatPower.actualLabel')}
                    </th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {members.map((m, memberIdx) => {
                const memberEmail = nameToEmail?.get(m.name.toLowerCase());
                const qt = quarterTotals[memberIdx];
                return (
                  <tr
                    key={m.name}
                    className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors"
                  >
                    <td className="py-1.5 px-2 font-medium">
                      {memberEmail ? (
                        <Link
                          to="/members/$email"
                          params={{ email: memberEmail }}
                          className="text-[var(--color-accent-primary)] hover:underline"
                        >
                          {m.name}
                        </Link>
                      ) : (
                        <span className="text-[var(--color-text-primary)]">{m.name}</span>
                      )}
                    </td>
                    {monthHeaders.map((mh, i) => {
                      const cm = m.months[i];
                      return (
                        <Fragment key={mh}>
                          <td className="py-1.5 px-2 text-right text-[var(--color-text-secondary)] border-l border-[var(--color-border)]">
                            {cm?.expectedPower != null ? formatNum(cm.expectedPower) : '-'}
                          </td>
                          <td className="py-1.5 px-2 text-right font-medium text-[var(--color-text-primary)] whitespace-nowrap">
                            {cm ? formatActualPowerDisplay(cm) : '-'}
                          </td>
                        </Fragment>
                      );
                    })}
                    <td className="py-1.5 px-2 text-right text-[var(--color-text-secondary)] border-l border-[var(--color-border)]">
                      {qt.expected !== null ? formatNum(qt.expected) : '-'}
                    </td>
                    <td className="py-1.5 px-2 text-right font-medium text-[var(--color-text-primary)] whitespace-nowrap">
                      {formatPowerPair(qt.completedPower, qt.totalPower, qt.hasWip)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[var(--color-border)] font-bold">
                <td className="py-2 px-2 text-[var(--color-text-primary)]">
                  {t('members.combatPower.teamTotal')}
                </td>
                {monthHeaders.map((mh, i) => {
                  const avg = monthAverages[i];
                  return (
                    <Fragment key={mh}>
                      <td className="py-2 px-2 text-right text-[var(--color-text-secondary)] border-l border-[var(--color-border)]">
                        {monthExpectedAverages[i] !== null
                          ? formatNum(monthExpectedAverages[i])
                          : '-'}
                      </td>
                      <td className="py-2 px-2 text-right text-[var(--color-text-primary)] whitespace-nowrap">
                        {formatPowerPair(avg.completedPower, avg.totalPower, avg.hasWip)}
                      </td>
                    </Fragment>
                  );
                })}
                <td className="py-2 px-2 text-right text-[var(--color-text-secondary)] border-l border-[var(--color-border)]">
                  {avgExpectedTotal !== null ? formatNum(avgExpectedTotal) : '-'}
                </td>
                <td className="py-2 px-2 text-right text-[var(--color-text-primary)] whitespace-nowrap">
                  {formatPowerPair(
                    avgQuarterActual.completedPower,
                    avgQuarterActual.totalPower,
                    avgQuarterActual.hasWip,
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
